import Immutable from 'seamless-immutable';
import _ from 'lodash';
import RefData from 'ref-data';

import { enums } from 'console-kit';
import { getMutable } from '../../common/Utils';
import channelService from '../../common/Services/channel-service';

import { assetRefService } from '../../common/Services';

/**
 * Channel
 * @typedef {Object} Channel
 * @property {string} assetRef - assetRef for the channel icon. handles null values
 * @property {string} domainId - id of the custom domain record for this channel
 * @property {string} domainName - text of the custom domain record for this channel
 * @property {string} icon - url for the channel icon. does NOT handle null values
 * @property {number} id - the channel id
 * @property {boolean} isConnected - is the channel currently connected
 * @property {boolean} isLive - is the channel a live/production channel
 * @property {boolean} isStaging - is the channel a staging channel
 * @property {string} name - the channel name
 * @property {string} publishMode - whether the channel is a staging or live channel (prefer isLive and isStaging)
 * @property {RefData} typeRef - the channel type ref
 * @property {RefData} connectedOn - the date the channel was last connected
 * @property {RefData} connectedBy - the user details of the person that connected the channel
 * @property {string} link - link to the social account page
 * @property {string} profileName - value used to map the id from social/media accounts
 * @property {object[]} statusList - object containing errors
 */

/**
 * Create a ChannelModel from provided data
 */
function create(data = {}) {
  const result = _.clone(data);

  result.typeRef = deserializeTypeCheck(data.typeRef);
  result.status = deserializeTypeCheck(data.status);
  result.publishMode = deserializeTypeCheck(data.publishMode);

  return Immutable(result, { prototype: model });
}

function deserializeTypeCheck(datum) {
  if (typeof datum === 'object' && datum.hasOwnProperty('id')) return RefData.Model.hydrate(datum.id);
  if (typeof datum === 'string') return RefData.Model.hydrate(datum);
}

function serializeTypeCheck(datum) {
  return _.get(datum, 'id', datum);
}

/**
 * Transforms a backend channel object into a frontend Channel Model
 * @param {Object} channel - backend formatted channel
 * @returns {Channel}
 */
function deserialize(channel) {
  const result = _.pick(channel, _keys);
  return create(result);
}

/**
 * Transform a ChannelModel into a backend channel object
 * @returns {Object}
 */
function serialize(channel) {
  const result = getMutable(channel, { deep: true });

  result.typeRef = serializeTypeCheck(channel.typeRef);
  result.status = serializeTypeCheck(channel.status);
  result.publishMode = serializeTypeCheck(channel.publishMode);

  return _.pick(result, _keys);
}

/** The ChannelModel prototype */
const model = {
  get assetRef() {
    return this.icon
      ? assetRefService.buildAssetRef('weburl', 'image', '100x100', this.icon)
      : assetRefService.buildAssetRef('static', 'image', '100x100', DEFAULT_ICONS[this.typeRef.code]);
  },
  get isLive() {
    return this.publishMode.id === RefData.Enum.MODE.PUBLISH.PRODUCTION;
  },
  get isStaging() {
    return this.publishMode.id === RefData.Enum.MODE.PUBLISH.STAGE;
  },
  get isDomainInactive() {
    return this.domainStatus === RefData.Enum.STATUS.DOMAIN.INACTIVE;
  },
  get connectionStatusLabel() {
    if (!!this.isStaging) return 'staging';
    if (!!this.isConnected) return 'live';
    return 'disconnected';
  },

  get channelTypeIcon() {
    switch (this.typeRef.id) {
      case RefData.Enum.TYPE.CHANNEL.FACEBOOK:
        return this.publishMode === RefData.Enum.MODE.PUBLISH.STAGE
          ? enums.icons.FACEBOOK_OUTLINE
          : enums.icons.FACEBOOK;
      case RefData.Enum.TYPE.CHANNEL.SNAPCHAT:
        return enums.icons.SNAPCHAT;
      case RefData.Enum.TYPE.CHANNEL.INSTAGRAM:
        return enums.icons.INSTAGRAM;
      case RefData.Enum.TYPE.CHANNEL.TWITTER:
        return enums.icons.TWITTER;
      case RefData.Enum.TYPE.CHANNEL.MICROSITE:
        return enums.icons.MICROSITE;
      default:
        return enums.icons.CLOUD;
    }
  },

  get invalidatedLabel() {
    return !!this.invalidated ? 'invalidated' : '';
  },

  get supportsPinnedContainers() {
    return this.typeRef.id === RefData.Enum.TYPE.CHANNEL.WEB;
  },

  get supportsContainers() {
    return _.includes(
      [
        RefData.Enum.TYPE.CHANNEL.FACEBOOK,
        RefData.Enum.TYPE.CHANNEL.INSTAGRAM,
        RefData.Enum.TYPE.CHANNEL.SNAPCHAT,
        RefData.Enum.TYPE.CHANNEL.TWITTER,
        RefData.Enum.TYPE.CHANNEL.WEB,
      ],
      this.typeRef.id
    );
  },

  get supportsFBApps() {
    return this.typeRef.id === RefData.Enum.TYPE.CHANNEL.FACEBOOK;
  },

  get supportsCampaigns() {
    return this.typeRef.id !== RefData.Enum.TYPE.CHANNEL.YOUTUBE;
  },

  get supportsCustomDomains() {
    return this.typeRef.id !== RefData.Enum.TYPE.CHANNEL.FACEBOOK;
  },

  get supportsDisconnect() {
    return this.typeRef.id !== RefData.Enum.TYPE.CHANNEL.WEB && this.typeRef.id !== RefData.Enum.TYPE.CHANNEL.SNAPCHAT;
  },

  get isDeprecated() {
    return channelService.isDeprecatedChannelType(this.typeRef.id);
  },
};

/**
 * An array of keys on the API Theme object that should be shared between the API requests and responses
 */
const _keys = [
  'aboutInfo',
  'connectedBy',
  'connectedOn',
  'domainId',
  'domainName',
  'domainStatus',
  'icon',
  'id',
  'isConnected',
  'link',
  'name',
  'publishMode',
  'status',
  'typeRef',
  'profileName',
  'statusList',
  'publishingManagers',
];

const DEFAULT_ICONS = {
  facebook:  'c/img/icons/feed/feed-facebook.svg',
  instagram: 'c/img/icons/feed/feed-instagram.svg',
  snapchat:  'c/img/icons/feed/feed-rss.svg', // TODO don't have a snapchat image yet
  twitter:   'c/img/icons/feed/feed-twitter.svg',
  web:       'c/img/icons/feed/feed-web.svg',
};

export default {
  create,
  deserialize,
  serialize,
};
